// Waiting, which is the hard part.
//
// ---------------------------------------------------------------------------
// SILENCE IS AN OUTCOME
// ---------------------------------------------------------------------------
//
// The facilitator writes at its own pace. A reply may come in four seconds, in forty
// minutes, or never — and never is CORRECT behaviour, not a failure. Somebody has just
// typed a paragraph about who should be in the room when they die. Telling that person
// that the system is broken because nothing answered would be the worst thing this
// interface could do, and it is what every default reaches for: a spinner with no end, a
// timeout, a "no response", a red bar.
//
// So this component has, in total, two states and no timers.
//
//   * `facilitatorBusy` is true: one quiet line. It says the facilitator is reading. It
//     does NOT say a reply is coming, does not count, does not animate toward anything,
//     and does not change if it stays true for an hour.
//   * `facilitatorBusy` is false: nothing at all. Not "idle", not "no reply", not a
//     dismissed state — the line is simply not there.
//
// The transition that matters most is busy → not busy with no new message. That is the
// facilitator having considered and chosen not to speak, and what it looks like here is
// the line disappearing and the conversation sitting exactly as it was. Which is right:
// nothing happened, so nothing is reported.
//
// ---------------------------------------------------------------------------
// WHY THERE IS NO TIMER IN THIS FILE
// ---------------------------------------------------------------------------
//
// Any elapsed-time behaviour — "still thinking…" after two minutes, an error after ten,
// even a gentle "this is taking a while" — is a claim about how long an answer should
// take, made by a client that has no idea. Ten minutes of quiet and ten seconds of quiet
// are the same screen. `test/waiting.test.tsx` advances a fake clock ten minutes with a
// live lease held and asserts that nothing on the page changed and that no text on it
// matches the vocabulary of failure.
//
// ---------------------------------------------------------------------------
// THE STANDING SENTENCE
// ---------------------------------------------------------------------------
//
// `WaitingIsNormal` below is rendered under the composer ALWAYS — busy or not, message
// sent or not, first visit or hundredth. That is the point of it. A reassurance that
// appears when the client decides you have waited too long is a reassurance that tells you
// something has gone wrong; a sentence that was there before you wrote anything is just
// how this place works.
//
// ---------------------------------------------------------------------------
// WHAT `facilitatorBusy` ACTUALLY MEANS, AND THE TWO WAYS IT CAN BE WRONG
// ---------------------------------------------------------------------------
//
// It is true while an activation lease exists and has not expired. An expired lease reads
// false, because a lease outlives the worker that took it and is only cleared when the
// next activation claims the plan — so a crashed worker does not leave a family looking at
// a facilitator that is permanently about to speak. That rule is the server's; this
// component's job is not to undo it, which is why `decodePlan` reads a missing field as
// false rather than as busy.
//
// It can be LATE, because the field is never pushed: the lease columns are outside the
// delivery layer's change fingerprint, so taking a lease signals nobody and the client
// learns about it on its next read. `usePlan` polls for exactly this reason and the whole
// argument is in its header. A missed "considering" costs a family nothing. Guessing that
// the facilitator is thinking because somebody sent a message would cost them a promise
// this interface must not make.

export type FacilitatorStateProps = {
  facilitatorBusy: boolean;
};

export function FacilitatorState({
  facilitatorBusy,
}: FacilitatorStateProps): JSX.Element {
  // The region is always in the tree so that assistive technology announces the line
  // arriving rather than a container appearing. Its emptiness is the not-busy state.
  return (
    <div className="facilitatorState" aria-live="polite" data-testid="facilitator-state">
      {facilitatorBusy ? <p className="facilitatorState__line">Mor is reading.</p> : null}
    </div>
  );
}

/**
 * Not currently rendered anywhere (`docs/archived/13-visual-refresh.md`) — pulled during the visual
 * refresh, with something in this spirit meant to come back later rather than staying gone.
 * The reasoning above for why it must be standing text, never a response to elapsed time,
 * still holds for whatever replaces it.
 */
export function WaitingIsNormal(): JSX.Element {
  return (
    <p className="waitingNote">
      Mor answers in its own time — sometimes straight away, sometimes much later, and
      sometimes not at all. You can close this and come back.
    </p>
  );
}
